import { getData as defaultGetData } from './httpClient';

export function resolveInvoiceDocumentId(invoice) {
  if (!invoice) return null;
  const raw = invoice.id ?? invoice.invoiceId ?? invoice.comprobanteId;
  if (raw == null || raw === '') return null;
  return String(raw);
}

export function extractPdfBase64FromDocument(document) {
  if (!document) {
    throw new Error('El servidor no devolvió el documento PDF');
  }

  if (typeof document === 'string') {
    return {
      base64: document.replace(/^data:[^;]+;base64,/, '').trim(),
      mimeType: 'application/pdf',
    };
  }

  const nested = document.document ?? document.pdf ?? document.archivo;
  if (nested && nested !== document) {
    return extractPdfBase64FromDocument(nested);
  }

  const rawData =
    document.data ??
    document.contenido ??
    document.content ??
    document.base64 ??
    document.archivo;

  const mimeType =
    document.mime_type ??
    document.mimeType ??
    document.content_type ??
    'application/pdf';

  if (typeof rawData !== 'string' || !rawData.trim()) {
    throw new Error('Formato de PDF no reconocido en la respuesta del servidor');
  }

  return {
    base64: rawData.replace(/^data:[^;]+;base64,/, '').trim(),
    mimeType,
  };
}

export function base64ToPdfBlob(base64Data, mimeType = 'application/pdf') {
  const normalized = String(base64Data || '')
    .replace(/^data:[^;]+;base64,/, '')
    .trim();
  if (!normalized) {
    throw new Error('El PDF recibido está vacío');
  }

  let byteCharacters;
  try {
    byteCharacters = atob(normalized);
  } catch {
    throw new Error('El contenido del PDF no es válido (base64 corrupto)');
  }

  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i += 1) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: mimeType || 'application/pdf' });
  if (!blob.size) {
    throw new Error('El PDF generado está vacío');
  }
  return blob;
}

export function buildInvoicePdfHints(row) {
  if (!row) return {};
  return {
    series: row.serie ?? row.series,
    number: row.numero ?? row.number,
    invoiceType: row.tipo_comprobante ?? row.invoiceType,
  };
}

export async function fetchInvoicePdfBlob(
  documentId,
  getData = defaultGetData,
  hints = {},
) {
  const id = String(documentId || '').trim();
  if (!id) {
    throw new Error('ID de comprobante inválido');
  }

  const encoded = encodeURIComponent(id);
  const params = new URLSearchParams();
  if (hints.series) params.set('series', String(hints.series));
  if (hints.number != null && hints.number !== '') {
    params.set('number', String(hints.number));
  }
  if (hints.invoiceType != null && hints.invoiceType !== '') {
    params.set('invoiceType', String(hints.invoiceType));
  }
  const query = params.toString();
  const response = await getData(
    `/invoices/document/${encoded}${query ? `?${query}` : ''}`,
  );
  const { base64, mimeType } = extractPdfBase64FromDocument(
    response?.payload?.document ?? response?.document,
  );
  return base64ToPdfBlob(base64, mimeType);
}

export function openPdfBlobInNewTab(blob) {
  const blobUrl = URL.createObjectURL(blob);
  window.open(blobUrl, '_blank', 'noopener,noreferrer');
  setTimeout(() => URL.revokeObjectURL(blobUrl), 15000);
}

export function downloadPdfBlob(blob, filename = 'factura.pdf') {
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(blobUrl), 15000);
}

export function buildInvoicePdfFilename(row) {
  const serie = row?.serie ?? row?.series ?? 'A';
  const numero = row?.numero ?? row?.number ?? row?.id ?? 'doc';
  return `factura-${serie}-${numero}.pdf`.replace(/\s+/g, '_');
}
